// The engraver: pure Song -> DrawnStaff. Composed of small passes, each
// independently testable as data (no DOM). Mirrors GP's staged
// ScoreEngravingContext. Single melody voice + chord symbols for now; the
// architecture extends to more voices without changing the shape. See SPEC §6.

import { chordSymbol } from '@/music/chords';
import { chordLabelSpaces, type ChordSymbolStyle } from './chordLabel';

// The chord-symbol dialect for the current engrave pass. Set once at each
// engrave entry point (a single synchronous call carries one opts), so the
// deep width-maths helpers can read it without threading through every
// signature. Not reentrant across differing styles — which never happens
// within one render.
let activeChordStyle: ChordSymbolStyle = 'triangle';
import { barVoices, type Bar, type MelodyNote, type Song } from '@/music/song';
import type {
  DrawnBeam,
  DrawnDynamic,
  DrawnLyric,
  DrawnMeasure,
  DrawnNote,
  DrawnRest,
  DrawnStaff,
  DrawnSystem,
  DrawnText,
  DrawnTuplet,
} from './drawn';
import { ledgerLineOffsets, spelledNote, staffOffset, type Clef } from './pitch';
import { keyAlterations, keySignature } from './keysig';
import { RhythmValue, beamLevel, beatsToRhythm, isBeamable, type Rhythm } from './rhythm';
import { relativeSpacing } from './spacing';
import { accidentalColumns } from './accidentals';
import { positionRests } from './restPlacement';
import { beamClearanceShift, type BeamObstacle } from './beamClearance';
import {
  fitLines,
  lineBars,
  scaledLineFit,
  systemWidthAt,
  type PlannedLine,
  type SystemGeometry,
} from './lines';
import {
  ENGRAVING,
  SMUFL,
  TUPLET_ENGRAVING,
  glyphVerticalBounds,
  glyphHorizontalBounds,
  ledgerHalfWidth,
  accidentalGlyph,
  articulationGlyph,
  dynamicGlyphs,
  flagFor,
  noteheadFor,
  slashNoteheadFor,
  ornamentGlyph,
  restFor,
  type SmuflGlyph,
} from './smufl';

const STAFF_LINES = 5;
const TOP_LINE_OFFSET = 4; // staffOffset of the treble top line (F5)
const MIDDLE_OFFSET = 2; // staffOffset of the middle line (B4)
const LEFT_PAD = 1.5; // staff-spaces before the first note in a measure
const RIGHT_PAD = 1.5; // trailing space before the barline
const ACCIDENTAL_WIDTH = 1.1;
// Minimum-width model for sparse bars (a chart bar with chords but little or
// no melody must still read as a bar, not collapse to its padding):
const EMPTY_BEAT_SPACES = 2.2; // min horizontal room per beat
const CHORD_GAP = 1.0; // clear space after a symbol before the next may start
const LYRIC_CHAR_SPACES = 0.9; // ≈ width of one syllable char at the renderer's 0.45em lyric font
const TEXT_CHAR_W = 0.84; // staff-spaces per char at the renderer's 0.42em

/** Form cues stay inside their bar, retaining authored paragraph breaks. */
function staffTextLines(text: string, maxChars: number): string[] {
  return text.split(/\r\n?|\n/).flatMap((paragraph) => {
    const lines: string[] = [];
    let line = '';
    for (let word of paragraph.trim().split(/\s+/)) {
      if (line && line.length + word.length + 1 > maxChars) {
        lines.push(line);
        line = '';
      }
      // Even an unbroken word must not run past the SVG's edge.
      while (word.length > maxChars) {
        lines.push(word.slice(0, maxChars));
        word = word.slice(maxChars);
      }
      line = line ? `${line} ${word}` : word;
    }
    return [...lines, line];
  });
}

export interface EngraveOptions {
  spaceSize?: number; // px per staff-space
  /** Available width for one system, in staff-spaces. */
  systemWidth?: number;
  /** Key label; overridden per-note by MelodyNote.spelledAs. Defaults to song.key. */
  key?: string;
  /** Staff clef. Treble renders the guitar-written G clef (8vb). */
  clef?: Clef;
  /**
   * Per-bar minimum widths (staff-spaces), keyed by global bar index. The
   * grand-staff alignment seam: engrave each staff once, take the per-bar
   * max, re-engrave both with the shared minimums — barlines then line up
   * and greedy system breaking (deterministic over equal widths) agrees.
   */
  minMeasureWidths?: number[];
  /** Extra ink sharing the main staff's harmony anchors (e.g. guitar voicings).
   * Signed extents relative to the anchor, in staff spaces; reserves room
   * before the first chord and between successive chords on that lane. */
  harmonyExtents?: { barIndex: number; beat: number; left: number; right: number }[];
  /**
   * Extra header room (staff-spaces) the renderer reserves on the FIRST
   * system only — the time signature. The first row's width budget and
   * justification target shrink by this much, so every system's right
   * margin lands on the same line despite the wider first-system header.
   */
  firstSystemIndent?: number;
  /**
   * Lead-sheet system breaking: cap each system at this many bars (the
   * printed book's four-to-a-line grid). Width stays the hard constraint —
   * a dense row still breaks earlier.
   */
  maxBarsPerSystem?: number;
  /**
   * Lead-sheet lines of exactly this many full bars (lines.ts): a pickup
   * rides on the first line uncounted, and a named section, a later ending or
   * a coda starts a new line. Replaces greedy breaking and maxBarsPerSystem.
   */
  barsPerLine?: number;
  /**
   * With barsPerLine: the page's width model and the device's default size
   * (100%). The lines are fitted at the default size, the whole staff shrinking
   * so they fit and, past the floor, splitting in half, and spaceSize scales
   * the result (the returned spaceSize; lines.ts `scaledLineFit`). Without it
   * the lines keep spaceSize and systemWidth, splitting any that overflow.
   */
  lineFit?: { geometry: SystemGeometry; baseSpaceSize: number };
  /** Chord-quality dialect for symbols (∆/maj7, m/−, ø/m7♭5). Default triangle. */
  chordSymbolStyle?: ChordSymbolStyle;
}

/** y (staff-spaces below top line) for a staffOffset (spaces above bottom line). */
const yFromOffset = (offset: number): number => TOP_LINE_OFFSET - offset;

interface LaidHead {
  sourceHeadIndex?: number;
  offset: number; // staffOffset
  y: number; // below top line
  accidental: SmuflGlyph | null;
  dx: number; // seconds step-aside (staff-spaces)
  accidentalDx?: number;
  midi: number;
  tie?: MelodyNote['tie'];
}

interface LaidNote {
  note: MelodyNote;
  value: RhythmValue;
  rhythm: Rhythm;
  offset: number; // staffOffset
  y: number; // below top line
  stemUp: boolean;
  accidental: SmuflGlyph | null;
  accidentalDx?: number;
  /** Any head carries an accidental (spacing must reserve the column). */
  hasAccidental: boolean;
  advance: number; // horizontal room this note consumes (staff-spaces)
  x: number; // notehead centre (assigned in the spacing pass)
  /** Fixed cross-voice head displacement; only the onset stretches. */
  columnDx?: number;
  /** Chord stack (bottom→top, incl. the top head) — absent for single notes. */
  heads?: LaidHead[];
}

/** Limit a beam's total rise, flattening tiny differences. */
export function beamEndpoints(y1: number, y2: number, flat = false): [number, number] {
  const maxSlope = flat ? 0 : ENGRAVING.beamSlopeMax;
  const minSlope = flat ? 0 : ENGRAVING.beamSlopeMin;
  const delta = y2 - y1;
  if (Math.abs(delta) < minSlope) return [y1, y1];
  return [y1, y1 + Math.sign(delta) * Math.min(Math.abs(delta), maxSlope)];
}

interface VoiceLayoutOpts {
  /** Two-voice engraving: force this voice's stems (voice 1 up, voice 2 down). */
  forceStemUp?: boolean;
  /** Rest line for this voice (staffOffset). Voices park rests apart. */
  restOffset?: number;
  /** Which clef positions the pitches (default treble). */
  clef?: Clef;
  /** Written quarters per Song beat (4/denominator): 1 in 4/4, 0.5 in 3/8 —
   *  so an eighth-note beat DRAWS as an eighth, not a quarter. */
  quarterPerBeat?: number;
}

/**
 * Pass 1: pitch, rhythm, stem direction, accidental, and advance per note.
 * Accidentals follow standard practice: draw one only when a note's alteration
 * differs from what's currently in effect for its line — the key signature, or
 * a prior accidental earlier in the same bar (so a natural cancels a sharp/flat
 * and a repeated alteration isn't re-drawn). State resets each bar. A note
 * tied over the barline carries its alteration without restating it, but not
 * to later notes in the bar (Gould): the next note on that line restates its
 * own, a cautionary one where it merely returns to the key signature.
 */
function layoutVoice(source: MelodyNote[], key?: string, opts: VoiceLayoutOpts = {}): LaidNote[] {
  const melody = [...source].sort((a, b) => a.beat - b.beat);
  const alters = keyAlterations(key);
  const barState = new Map<string, number>(); // letter+octave -> alteration in effect
  const carried = new Map<string, number>(); // lines a tie brought over the barline
  const restOffset = opts.restOffset ?? MIDDLE_OFFSET;

  /** Pitch → staff position + accidental, through the shared bar state, so a
   *  chord's inner heads follow the same courtesy-accidental rules. */
  const spellHead = (
    midi: number,
    spelledAs?: string,
    tie?: MelodyNote['tie'],
  ): Omit<LaidHead, 'dx'> => {
    const offset = staffOffset(midi, { key, spelledAs, clef: opts.clef });
    const { letter, octave, accidental } = spelledNote(midi, { key, spelledAs });
    const alter = accidental === 'sharp' ? 1 : accidental === 'flat' ? -1 : 0;
    const stateKey = `${letter}${octave}`;
    const identity = { midi, ...(tie ? { tie } : {}) };
    if ((tie === 'stop' || tie === 'both') && !barState.has(stateKey)) {
      // The tie crossed the barline: the started note printed the accidental.
      carried.set(stateKey, alter);
      return { offset, y: yFromOffset(offset), accidental: null, ...identity };
    }
    const inEffect = barState.has(stateKey) ? barState.get(stateKey)! : (alters.get(letter) ?? 0);
    const cautionary = !barState.has(stateKey) && (carried.get(stateKey) ?? alter) !== alter;
    let accGlyph: SmuflGlyph | null = null;
    if (alter !== inEffect || cautionary) {
      accGlyph = accidentalGlyph(alter > 0 ? 'sharp' : alter < 0 ? 'flat' : 'natural');
      barState.set(stateKey, alter);
    }
    return { offset, y: yFromOffset(offset), accidental: accGlyph, ...identity };
  };

  const qpb = opts.quarterPerBeat ?? 1;
  const out = melody.map((note) => {
    // Sounding length owns timing/spacing; written length owns the actual
    // notation. They differ inside explicit tuplets. Inferring both from the
    // sounding duration turned a 5:4 written sixteenth (1/5 quarter) into a
    // dotted 32nd with three beams.
    const spacingRhythm = beatsToRhythm(note.duration * qpb);
    const rhythm =
      note.writtenDuration == null ? spacingRhythm : beatsToRhythm(note.writtenDuration * qpb);
    const value = rhythm.base;
    if (note.rest || note.slash) {
      return {
        note,
        value,
        rhythm,
        offset: note.slash ? MIDDLE_OFFSET : restOffset,
        y: yFromOffset(note.slash ? MIDDLE_OFFSET : restOffset),
        stemUp: opts.forceStemUp ?? !note.slash,
        accidental: null,
        hasAccidental: false,
        advance: relativeSpacing(spacingRhythm.base) + rhythm.dots * ENGRAVING.dotAdvance,
        x: 0,
      };
    }
    const top = spellHead(note.midi, note.spelledAs, note.tie);

    // A chord on one stem: stack every head (bottom→top), step seconds aside,
    // and let the head FARTHEST from the middle line pick the stem direction.
    let heads: LaidHead[] | undefined;
    if (note.chordNotes?.length) {
      heads = [
        { ...top, dx: 0, sourceHeadIndex: note.sourceHeadIndex },
        ...note.chordNotes.map((c) => ({
          ...spellHead(c.midi, c.spelledAs, c.tie),
          dx: 0,
          sourceHeadIndex: c.sourceHeadIndex,
        })),
      ].sort((a, b) => b.y - a.y);
      for (let i = 1; i < heads.length; i++) {
        // Seconds rule (as the voicing staff): adjacent (or unison) heads
        // can't share a column — the upper steps aside, zigzagging clusters.
        if (heads[i - 1].y - heads[i].y < 1 && !heads[i - 1].dx) {
          heads[i].dx = ENGRAVING.noteheadWidth;
        }
      }
    }
    const farthest = heads
      ? heads.reduce((f, h) => (Math.abs(h.y - 2) > Math.abs(f.y - 2) ? h : f))
      : top;
    const anyAccidental = heads ? heads.some((h) => h.accidental) : !!top.accidental;

    return {
      note,
      value,
      rhythm,
      offset: top.offset,
      y: top.y,
      stemUp: opts.forceStemUp ?? farthest.y >= 2,
      accidental: top.accidental,
      hasAccidental: anyAccidental,
      // A grace steals a fixed sliver before its principal instead of
      // rhythm-proportional room — it occupies no musical time. A sung note
      // must also clear its WIDEST syllable (plus a word gap), or dense runs
      // mush their verses together — lyric width drives spacing, the real
      // engraving rule.
      advance: note.grace
        ? 1.4 + (anyAccidental ? ACCIDENTAL_WIDTH : 0)
        : Math.max(
            relativeSpacing(spacingRhythm.base) +
              rhythm.dots * ENGRAVING.dotAdvance +
              (anyAccidental ? ACCIDENTAL_WIDTH : 0),
            // Array.from, NOT .map: a sparse lyrics array (verse 2 sung
            // where verse 1 has run out) keeps holes through .map, and a
            // spread hole reaches Math.max as undefined → NaN bar width.
            note.lyrics?.length
              ? Math.max(...Array.from(note.lyrics, (syl) => syl?.text.length ?? 0)) *
                  LYRIC_CHAR_SPACES +
                  0.7
              : 0,
          ),
      x: 0,
      ...(heads ? { heads } : {}),
    };
  });
  // Grace notes take their PRINCIPAL's stem direction, not their own pitch
  // rule — a turn's graces belong to the note they ornament, and beaming a
  // high pair downward put a blob below the staff while the melody stems up.
  for (let i = 0; i < out.length; i++) {
    if (!out[i].note.grace) continue;
    for (let j = i + 1; j < out.length; j++) {
      if (out[j].note.grace) continue;
      if (!out[j].note.rest) out[i] = { ...out[i], stemUp: out[j].stemUp };
      break;
    }
  }
  // Syllables are CENTRED on their notes, so consecutive sung notes need
  // half of BOTH syllables between them — a short word before a long one
  // ("was" → "schmeckt") mushed under own-width-only reservation.
  const lyricChars = (n: LaidNote) =>
    n.note.lyrics?.length
      ? Math.max(...Array.from(n.note.lyrics, (syl) => syl?.text.length ?? 0))
      : 0;
  for (let i = 0; i < out.length; i++) {
    const a = lyricChars(out[i]);
    if (!a) continue;
    for (let j = i + 1; j < out.length; j++) {
      const b = lyricChars(out[j]);
      if (!b) continue;
      out[i] = {
        ...out[i],
        advance: Math.max(out[i].advance, ((a + b) / 2) * LYRIC_CHAR_SPACES + 0.7),
      };
      break;
    }
  }
  return out;
}

/** One staff's accidental columns at an onset, after cross-voice head shifts.
 * Offsets are fixed glyph geometry; justification stretches onset positions,
 * not the space between an accidental and its owning head. */
function positionAccidentals(notes: LaidNote[], columnLeft: number): number {
  const pitched = notes.filter((n) => !n.note.rest);
  const entries = pitched.flatMap((n) =>
    (n.heads ?? [n])
      .filter((head) => head.accidental != null)
      .map((head) => ({ n, head, scale: headScaleOf(n.note) })),
  );
  if (!entries.length) return 0;
  const headLeft = Math.min(
    ...pitched.flatMap((n) => {
      const glyph = n.note.slash
        ? slashNoteheadFor(n.note.slash === 'beat' ? RhythmValue.Quarter : n.value).glyph
        : noteheadFor(n.value);
      const left = glyphHorizontalBounds(glyph).left * headScaleOf(n.note);
      return (n.heads ?? [n]).map(
        (head) =>
          n.x +
          ('dx' in head ? head.dx : 0) +
          (ledgerLineOffsets(head.offset).length
            ? Math.min(left, -ledgerHalfWidth(glyph, headScaleOf(n.note)))
            : left),
      );
    }),
  );
  const { placements, left } = accidentalColumns(
    entries.map(({ head, scale }) => ({
      glyph: head.accidental!,
      y: head.y,
      scale,
    })),
    headLeft - 0.3,
  );
  entries.forEach(({ n, head }, i) => {
    if (placements[i].duplicate) head.accidental = null;
    else head.accidentalDx = placements[i].x - n.x;
  });
  for (const n of pitched) {
    const top = n.heads?.find((head) => head.y === n.y);
    if (top) {
      n.accidental = top.accidental;
      n.accidentalDx = top.accidentalDx;
    }
  }
  return Math.max(ACCIDENTAL_WIDTH, columnLeft - left);
}

/** Pass 2: cumulative x. Returns the measure content width. */
function spaceHorizontally(laid: LaidNote[]): number {
  let running = LEFT_PAD;
  for (const n of laid) {
    n.x = running + ENGRAVING.noteheadWidth / 2;
    const accidentalWidth = positionAccidentals([n], running);
    running += accidentalWidth;
    n.x += accidentalWidth;
    running += n.advance - (n.hasAccidental ? ACCIDENTAL_WIDTH : 0);
  }
  return running + RIGHT_PAD;
}

/**
 * Pass 2 for TWO voices: a shared onset grid. Notes sounding on the same beat
 * (in either voice) share one x column. A column's advance is DISTRIBUTED by
 * duration: a note's width claim spreads across every onset gap it sounds
 * through, so a bass half note under a treble triplet doesn't dump its whole
 * width into the triplet's first gap (which drew the triplet uneven) — each
 * gap takes the max, over sounding notes, of advance × (gap ÷ duration).
 * Single-voice bars keep spaceHorizontally untouched.
 */
function spaceColumns(voices: LaidNote[][], staffOf?: number[]): number {
  const columns = new Map<number, { n: LaidNote; vi: number }[]>();
  voices.forEach((voice, vi) => {
    for (const n of voice) {
      const beatKey = Math.round(n.note.beat * 1e6) / 1e6;
      const col = columns.get(beatKey);
      if (col) col.push({ n, vi });
      else columns.set(beatKey, [{ n, vi }]);
    }
  });
  const orderedEntries = [...columns.entries()].sort((a, b) => a[0] - b[0]);
  const ordered = orderedEntries.map(([, col]) => col);
  const beats = orderedEntries.map(([b]) => b);
  const allNotes = voices.flat();
  const netAdvance = (n: LaidNote) => n.advance - (n.hasAccidental ? ACCIDENTAL_WIDTH : 0);
  // A note's advance is two things: fixed GLYPH width (head + dots + a chord
  // stack's zigzag — must clear before the next column whatever the rhythm
  // says) and duration-proportional room (distributable across the gaps the
  // note sounds through). Only the second dilutes.
  const glyphNeed = (n: LaidNote): number => {
    const scale = headScaleOf(n.note);
    const zigzag = n.heads ? Math.max(...n.heads.map((h) => h.dx)) : 0;
    const glyph = n.note.slash ? slashNoteheadFor(n.value).glyph : noteheadFor(n.value);
    const hasLedger =
      !n.note.rest && (n.heads ?? [n]).some((head) => ledgerLineOffsets(head.offset).length > 0);
    const width = Math.max(
      headWidthOf(n) * scale,
      hasLedger ? 2 * ledgerHalfWidth(glyph, scale) : 0,
    );
    return width + zigzag + n.rhythm.dots * ENGRAVING.dotAdvance + 0.4;
  };
  /** Width of the gap from column k's beat to the next column (or barline). */
  const gapAdvance = (k: number): number => {
    const from = beats[k];
    const to = k + 1 < beats.length ? beats[k + 1] : Infinity;
    let adv = 1.5; // floor: columns never fuse
    for (const n of allNotes) {
      const dur = Math.max(n.note.duration, 1e-6);
      const starts = Math.abs(n.note.beat - from) < 1e-6;
      // This column's own glyphs must fit before the next column.
      if (starts && !n.note.rest) adv = Math.max(adv, glyphNeed(n));
      // Graces occupy fixed engraved space, not musical time — their column
      // keeps the full sliver whatever the beat arithmetic says.
      if (n.note.grace) {
        if (starts) adv = Math.max(adv, netAdvance(n));
        continue;
      }
      if (n.note.beat <= from + 1e-6 && n.note.beat + dur > from + 1e-6) {
        const share =
          to === Infinity ? (n.note.beat + dur - from) / dur : Math.min(1, (to - from) / dur);
        adv = Math.max(adv, netAdvance(n) * share);
      }
    }
    return adv;
  };
  let running = LEFT_PAD;
  let k = -1;
  for (const col of ordered) {
    k++;
    for (const { n } of col) n.x = running + ENGRAVING.noteheadWidth / 2;
    // Cross-voice step-aside: two voices sounding a unison or second at the
    // same onset can't share a column — the stem-DOWN voice's note steps
    // right of the stem-up one (Gould's rule), heads clear, stems intact.
    const headYs = (n: LaidNote) => (n.heads ? n.heads.map((h) => h.y) : [n.y]);
    let stepped = false;
    for (let i = 0; i < col.length; i++) {
      for (let j = i + 1; j < col.length; j++) {
        const a = col[i];
        const b = col[j];
        if (a.vi === b.vi || a.n.note.rest || b.n.note.rest) continue;
        // Voices on DIFFERENT staves share the onset grid but never step
        // aside — their y values live in different clef spaces, and a treble
        // F4 is not "a second from" a bass D3 just because the numbers say so.
        if ((staffOf?.[a.vi] ?? 0) !== (staffOf?.[b.vi] ?? 0)) continue;
        if (a.n.stemUp === b.n.stemUp) continue;
        const collides = headYs(a.n).some((ya) => headYs(b.n).some((yb) => Math.abs(ya - yb) < 1));
        if (!collides) continue;
        // An EXACT unison of equal written value shares one notehead (the
        // classic two-stems-one-head look): let the heads coincide — up-stem
        // on the right edge, down-stem on the left. Stepping such a pair
        // aside reads as two repeated notes instead of one doubled note.
        if (
          a.n.y === b.n.y &&
          a.n.value === b.n.value &&
          a.n.rhythm.dots === b.n.rhythm.dots &&
          !a.n.heads &&
          !b.n.heads
        )
          continue;
        const down = a.n.stemUp ? b.n : a.n;
        if (down.x === running + ENGRAVING.noteheadWidth / 2) {
          down.x += ENGRAVING.noteheadWidth;
          down.columnDx = ENGRAVING.noteheadWidth;
          stepped = true;
        }
      }
    }
    // Staves share onset x positions, but their accidentals pack independently.
    const perStaff = new Map<number, LaidNote[]>();
    for (const { n, vi } of col) {
      const staff = staffOf?.[vi] ?? 0;
      const members = perStaff.get(staff) ?? [];
      members.push(n);
      perStaff.set(staff, members);
    }
    const acc = Math.max(
      0,
      ...[...perStaff.values()].map((notes) => positionAccidentals(notes, running)),
    );
    running += acc;
    for (const { n } of col) n.x += acc;
    running += gapAdvance(k);
    if (stepped) running += ENGRAVING.noteheadWidth;
  }
  return running + RIGHT_PAD;
}

function headWidthOf(n: LaidNote): number {
  return n.note.slash ? slashNoteheadFor(n.note.slash === 'beat' ? RhythmValue.Quarter : n.value).width
    : ENGRAVING.noteheadWidth;
}

/** Size scale for a note's drawn head — the stem must anchor to the edge of
 *  the head the RENDERER draws (65% grace / 70% cue), not the full-size one. */
function headScaleOf(note: MelodyNote): number {
  return note.grace ? ENGRAVING.graceScale : note.cue ? ENGRAVING.cueScale : 1;
}

/** Stem-length scale: reduced notes take proportionally shorter stems. */
function stemScaleOf(note: MelodyNote): number {
  return note.grace ? ENGRAVING.graceStemScale : note.cue ? ENGRAVING.cueScale : 1;
}

/** Complete explicit tuplets, including rests. Identity wins over meter and
 * member count; older Song fixtures without identity use their musical end. */
function tupletRuns(laid: LaidNote[]): number[][] {
  const runs: number[][] = [];
  let run: number[] = [];
  let runEnd = 0;
  let fallbackEnd = 0;
  const flush = () => {
    if (run.length) runs.push(run);
    run = [];
  };
  laid.forEach((n, i) => {
    if (n.note.grace) return;
    if (n.note.tuplet == null) {
      flush();
      return;
    }
    const first = run.length ? laid[run[0]].note : undefined;
    if (
      first &&
      (Math.abs(n.note.beat - runEnd) > 1e-3 ||
        n.note.tupletGroup !== first.tupletGroup ||
        n.note.tuplet !== first.tuplet ||
        (first.tupletGroup == null &&
          isBeamable(laid[run[0]].value) &&
          n.note.beat >= fallbackEnd - 1e-6))
    )
      flush();
    if (!run.length) fallbackEnd = n.note.beat + n.note.tuplet * n.note.duration;
    run.push(i);
    runEnd = n.note.beat + n.note.duration;
  });
  flush();
  return runs;
}

/** Pass 3: group consecutive beamable notes, breaking at ordinary rests and beam-group
 *  starts. The group unit comes from the meter: eighth-denominator meters
 *  beam in threes (the whole bar in 3/8, per dotted crotchet in 6/8), quarter
 *  meters per beat. */
function groupBeams(laid: LaidNote[], groupBeats = 1, tuplets = tupletRuns(laid)): number[][] {
  const groups: number[][] = [];
  let current: number[] = [];
  const flush = () => {
    if (current.length > 1) groups.push(current);
    current = [];
  };
  let graceRun: number[] = [];
  const flushGrace = () => {
    if (graceRun.length > 1) groups.push(graceRun);
    graceRun = [];
  };
  const tupletOf = new Map(tuplets.flatMap((run, k) => run.map((i) => [i, k] as const)));
  let previousTuplet: number | undefined;
  laid.forEach((n, i) => {
    // A grace never breaks the principals' beam, but a RUN of graces beams
    // among itself (the turn pairs of the reference share a mini beam); a
    // lone grace keeps its own flag.
    if (n.note.grace) {
      if (isBeamable(n.value)) graceRun.push(i);
      else flushGrace();
      return;
    }
    flushGrace();
    const onBeat = Number.isInteger((n.note.beat - 1) / groupBeats);
    const tuplet = tupletOf.get(i);
    const sameTuplet = tuplet != null && tuplet === previousTuplet;
    if (!sameTuplet && (onBeat || tuplet !== previousTuplet) && current.length) flush();
    previousTuplet = tuplet;
    if (n.note.slash === 'beat' || !isBeamable(n.value) || (n.note.rest && tuplet == null)) {
      flush();
      return;
    }
    // Interior tuplet rests take space under the beam but have no stem.
    if (!n.note.rest) current.push(i);
  });
  flush();
  flushGrace();
  return groups;
}
/** Keep a tuplet's horizontal group until its notes and marks have been drawn.
 * Placement must use glyph edges and the final stems, including grace flips. */
interface PendingTuplet {
  figure: DrawnTuplet;
  fromX: number;
  toX: number;
}

interface TupletBatch {
  pending: PendingTuplet[];
  notes: DrawnNote[];
  rests: DrawnRest[];
  beams: DrawnBeam[];
}

interface BeamBatch {
  indices: number[];
  beams: DrawnBeam[];
}

function beamObstacles(laid: LaidNote[]): BeamObstacle[] {
  const boxes: BeamObstacle[] = [];
  for (const n of laid) {
    if (n.note.rest) continue;
    const scale = headScaleOf(n.note);
    const headGlyph = n.note.slash
      ? slashNoteheadFor(n.note.slash === 'beat' ? RhythmValue.Quarter : n.value).glyph
      : noteheadFor(n.value);
    const glyph = (cp: SmuflGlyph, x: number, y: number) => {
      const h = glyphHorizontalBounds(cp),
        v = glyphVerticalBounds(cp);
      boxes.push({
        left: x + h.left * scale,
        right: x + h.right * scale,
        top: y + v.top * scale,
        bottom: y + v.bottom * scale,
      });
    };
    for (const head of n.heads ?? [{ ...n, dx: 0 }]) {
      glyph(headGlyph, n.x + head.dx, head.y);
      if (head.accidental != null)
        glyph(head.accidental, n.x + (head.accidentalDx ?? -scale), head.y);
      const half = ledgerHalfWidth(headGlyph, scale);
      for (const y of ledgerLineOffsets(head.offset).map(yFromOffset))
        boxes.push({
          left: n.x + head.dx - half,
          right: n.x + head.dx + half,
          top: y - ENGRAVING.staffLineThickness / 2,
          bottom: y + ENGRAVING.staffLineThickness / 2,
        });
    }
  }
  return boxes;
}

/** Pass 4: beam geometry + stem extension. Mutates stem tips of grouped notes.
 *  `stemLenScale` shortens a MIDDLE voice's beamed stems (0.75) so its beam
 *  hugs its own heads instead of diving through the neighbouring voice. */
function computeBeams(
  laid: LaidNote[],
  groups: number[][],
  stems: Map<number, DrawnNote['stem']>,
  forceStemUp?: boolean,
  tupletsOut?: PendingTuplet[],
  stemLenScale = 1,
  batches?: BeamBatch[],
): DrawnBeam[] {
  const beams: DrawnBeam[] = [];
  for (const group of groups) {
    const beamStart = beams.length;
    const notes = group.map((i) => laid[i]);
    const upVotes = notes.filter((n) => n.stemUp).length;
    // Choose the principal group's direction from its complete pitch range.
    // Counting votes lets repeated middle notes outweigh an extreme head and
    // can put an otherwise identical passage's beam on the opposite side.
    const headYs = notes.flatMap((n) => n.heads?.map((h) => h.y) ?? [n.y]);
    const naturalUp = (Math.min(...headYs) + Math.max(...headYs)) / 2 >= 2;
    const useNoteDirections = notes.every((n) => n.note.grace) || notes.every((n) => n.note.slash);
    const stemUp = forceStemUp ?? (useNoteDirections ? upVotes * 2 >= notes.length : naturalUp);
    const dir = stemUp ? -1 : 1; // up = smaller y
    const first = notes[0];
    const last = notes[notes.length - 1];
    const stemX = (n: LaidNote) =>
      n.x + (stemUp ? headWidthOf(n) / 2 : -headWidthOf(n) / 2) * headScaleOf(n.note);
    const bx1 = stemX(first);
    const bx2 = stemX(last);
    const tip1 = first.y + dir * ENGRAVING.stemLength * stemLenScale * stemScaleOf(first.note);
    const tip2 = last.y + dir * ENGRAVING.stemLength * stemLenScale * stemScaleOf(last.note);
    let [by1, by2] = beamEndpoints(tip1, tip2);
    const beamYAt = (x: number): number =>
      bx2 === bx1 ? by1 : by1 + ((by2 - by1) * (x - bx1)) / (bx2 - bx1);
    // The slope cap can leave an inner note of a steep run with almost no
    // stem — its head touching the beam. Enforce a minimum stem by shifting
    // the whole (capped-slope) beam outward until every note clears it.
    // Multi-level groups need MORE: subdivision beams stack inward, so the
    // minimum grows by one beam step per extra level or the 16th-level beam
    // sits right on the noteheads (the "als die" bar read cramped).
    const maxLevel = Math.max(...notes.map((n) => beamLevel(n.value)));
    const MIN_STEM = 2.2 + (maxLevel - 1) * (ENGRAVING.beamThickness + ENGRAVING.beamSpacing);
    let shift = 0;
    for (const n of notes) {
      const innermost = n.heads
        ? stemUp
          ? Math.min(...n.heads.map((h) => h.y))
          : Math.max(...n.heads.map((h) => h.y))
        : n.y;
      const scale = stemScaleOf(n.note);
      const have = dir * (beamYAt(stemX(n)) - innermost); // stem length toward the beam
      shift = Math.max(shift, MIN_STEM * scale - have);
    }
    // A beam joining notes across an interior tuplet rest must clear the
    // entire rest glyph and its inward beam stack, not just the rest anchor.
    for (const n of laid.slice(group[0] + 1, group[group.length - 1])) {
      if (!n.note.rest) continue;
      const scale = headScaleOf(n.note);
      const bounds = glyphVerticalBounds(restFor(n.value));
      const edge = n.y + (stemUp ? bounds.top : bounds.bottom) * scale;
      const inset = (maxLevel - 1) * (ENGRAVING.beamThickness + ENGRAVING.beamSpacing);
      // Account for the slope over the glyph's horizontal footprint too.
      const ys = [beamYAt(n.x - 0.7 * scale), beamYAt(n.x + 0.7 * scale)];
      const nearest = stemUp ? Math.max(...ys) : Math.min(...ys);
      const have = dir * (nearest - edge);
      shift = Math.max(shift, inset + ENGRAVING.beamThickness / 2 + 0.5 - have);
    }
    if (shift > 0) {
      by1 += dir * shift;
      by2 += dir * shift;
    }
    // Stem x anchors follow each note's DRAWN head size (a cue passage beams
    // at 70%; stems must meet the smaller head's edge, not the full-size one).
    for (const i of group) {
      const n = laid[i];
      // A chord's stem must reach its head farthest from the beam.
      const anchor = n.heads
        ? stemUp
          ? Math.max(...n.heads.map((h) => h.y))
          : Math.min(...n.heads.map((h) => h.y))
        : n.y;
      stems.set(i, {
        x: stemX(n),
        y1: anchor + (n.note.slash ? (stemUp ? -0.656 : 0.664) * headScaleOf(n.note) : 0),
        y2: beamYAt(stemX(n)),
      });
    }
    // Beam levels are PER NOTE, not per group: a mixed group (the Telemann's
    // dotted-16th + 32nd + eighths) used to collapse to the group MINIMUM,
    // drawing four even "eighths" — the rhythm was unreadable. Level 1 spans
    // the group; each higher level draws contiguous runs of notes that carry
    // it, and an isolated note gets a fractional stub pointing at the
    // neighbour it subdivides (the previous note by convention).
    const graceGroup = notes.every((n) => n.note.grace);
    const seg = (xa: number, xb: number, level: number) => {
      // Subdivision beams stack INWARD from the primary, toward the heads —
      // the primary sits at the stem tips (a stub drawn outward floats
      // disconnected above a stems-up group, as the live-site Telemann bar 8
      // showed). dir points outward, so inward is -dir.
      const off =
        -dir *
        (level - 1) *
        (ENGRAVING.beamThickness + ENGRAVING.beamSpacing) *
        (graceGroup ? ENGRAVING.graceScale : 1);
      beams.push({
        x1: xa,
        y1: beamYAt(xa) + off,
        x2: xb,
        y2: beamYAt(xb) + off,
        level,
        ...(graceGroup ? { grace: true } : {}),
      });
    };
    seg(bx1, bx2, 1);
    const STUB = 1.2 * (graceGroup ? ENGRAVING.graceScale : 1);
    for (let level = 2; level <= maxLevel; level++) {
      let runStart = -1;
      const flushRun = (end: number) => {
        if (runStart < 0) return;
        if (end > runStart) seg(stemX(notes[runStart]), stemX(notes[end]), level);
        else {
          const x = stemX(notes[runStart]);
          if (runStart > 0) seg(x - STUB, x, level);
          else seg(x, x + STUB, level);
        }
        runStart = -1;
      };
      for (let k = 0; k <= notes.length; k++) {
        const has = k < notes.length && beamLevel(notes[k].value) >= level;
        // An eighth rest may sit under the primary beam, but must interrupt
        // a sixteenth-level beam between its neighbours.
        const restBreak =
          k > 0 &&
          k < notes.length &&
          laid
            .slice(group[k - 1] + 1, group[k])
            .some((n) => n.note.rest && beamLevel(n.value) < level);
        if (restBreak) flushRun(k - 1);
        if (has && runStart < 0) runStart = k;
        if (!has) flushRun(k - 1);
      }
    }
    batches?.push({ indices: group, beams: beams.slice(beamStart) });
    // A group of triplet-family members carries its figure ("3", "6") centred
    // on the group and ABOVE it — the reference engraving's (vocal) practice,
    // whatever the stem direction: above the beam for stems-up, above the
    // noteheads for stems-down (a below-the-beam figure would hang into the
    // inter-system band and the future lyric lane). A forced-down second
    // voice keeps the beam side so its figure doesn't invade voice 1.
    const inferredTriplet = notes.every((n) => n.note.tuplet == null && n.rhythm.triplet);
    if (tupletsOut && inferredTriplet) {
      // Legacy Song runs without explicit grouping infer the member count.
      const size = notes.length;
      tupletsOut.push({
        figure: {
          x: (first.x + last.x) / 2,
          y: 0,
          label: String(size),
          ...(forceStemUp === false ? { below: true } : {}),
        },
        fromX: first.x,
        toX: last.x,
      });
    }
  }
  return beams;
}

/**
 * x for a beat, aligned to the melody notes' actual (duration-spaced) x rather
 * than a linear beat fraction — so chord symbols and the voicing/tab stacks
 * keyed off them sit directly over the note sounding on that beat. Falls back to
 * a linear fraction only when the bar has no notes.
 */
function xAtBeat(laid: LaidNote[], beat: number, beats: number, width: number): number {
  const linear = LEFT_PAD + ((beat - 1) / beats) * (width - LEFT_PAD - RIGHT_PAD);
  if (laid.length === 0) return linear;
  const exact = laid.find((n) => Math.abs(n.note.beat - beat) < 1e-6);
  if (exact) return exact.x;
  const before = laid
    .filter((n) => n.note.beat <= beat)
    .sort((a, b) => b.note.beat - a.note.beat)[0];
  const after = laid
    .filter((n) => n.note.beat >= beat)
    .sort((a, b) => a.note.beat - b.note.beat)[0];
  // Missing neighbours become VIRTUAL anchors at the bar edges (start = beat
  // 1 at LEFT_PAD, end = beat beats+1 at the right pad): a beat past the last
  // onset walks toward the barline instead of parking on the last note — two
  // chord symbols over a whole-note bar used to pile up on its notehead.
  const bx = before?.x ?? LEFT_PAD;
  const bb = before?.note.beat ?? 1;
  const ax = after?.x ?? width - RIGHT_PAD;
  const ab = after?.note.beat ?? beats + 1;
  if (Math.abs(ab - bb) < 1e-6) return bx;
  const t = (beat - bb) / (ab - bb);
  return bx + t * (ax - bx);
}

/** Playable chords plus supplementary display-only harmony marks. */
function displayedHarmony(bar: Bar): { sourceId?: string; beat: number; text: string }[] {
  return [
    ...bar.chords.map((c) => ({
      sourceId: c.sourceId,
      beat: c.beat,
      // The source's own symbol wins ("G7/D", "Am", "B7alt"); the catalogue
      // label is the fallback for chart-native chords.
      text: c.symbol ?? chordSymbol(c.root, c.chordType),
    })),
    ...(bar.harmonyLabels ?? []).map((label) => ({
      sourceId: label.sourceId,
      beat: label.beat,
      text: label.text,
    })),
  ].sort((a, b) => a.beat - b.beat);
}

/**
 * Reserve harmony locally on the shared note grid. A late anticipation needs
 * room for its label after that onset, not a whole bar stretched in inverse
 * proportion to the short duration left before the barline.
 *
 * Keep the resulting anchors: re-interpolating from the lead voice after
 * moving columns loses onsets that exist only in another voice or staff.
 */
function reserveHarmonySpace(
  bars: Bar[],
  laid: LaidBar[],
  initialWidth: number,
  extents: { beat: number; left: number; right: number }[] = [],
): { width: number; chordXs: number[][] } {
  const lanes = bars.map(displayedHarmony);
  if (lanes.every((lane) => lane.length === 0)) {
    return { width: initialWidth, chordXs: lanes.map(() => []) };
  }
  const principalByBeat = new Map<number, LaidNote>();
  for (const n of laid.flatMap((lb) => lb.voices.flat())) {
    if (n.note.grace) continue;
    const current = principalByBeat.get(n.note.beat);
    // A stepped-aside second voice must not move the shared onset anchor.
    if (!current || n.x < current.x) principalByBeat.set(n.note.beat, n);
  }
  const principals = [...principalByBeat.values()].sort((a, b) => a.note.beat - b.note.beat);
  const beats = bars[0].beats || 4;
  const pad = Math.max(...bars.map(openRepeatPad));
  const baseX = (beat: number) => principals.length
    ? xAtBeat(principals, beat, beats, initialWidth)
    : LEFT_PAD + pad + ((beat - 1) / beats) * (initialWidth - LEFT_PAD - pad - RIGHT_PAD);
  // Simultaneous harmony labels share an onset but occupy successive places
  // in their lane. Different staves may have independent harmony lanes.
  const groups = lanes.map((lane, laneIndex) => {
    const byBeat = new Map<number, number>();
    for (const c of lane) {
      const previous = byBeat.get(c.beat);
      byBeat.set(
        c.beat,
        (previous == null ? 0 : previous + CHORD_GAP) + chordLabelSpaces(c.text, activeChordStyle),
      );
    }
    return [...byBeat].map(([beat, width]) => {
      const ink = laneIndex === 0 ? extents.filter((e) => Math.abs(e.beat - beat) < 1e-6) : [];
      return {
        beat,
        width: Math.max(width, ...ink.map((e) => e.right)),
        left: Math.min(0, ...ink.map((e) => e.left)),
      };
    });
  });
  const harmonyBeats = [...new Set(groups.flatMap((lane) => lane.map((c) => c.beat)))].sort(
    (a, b) => a - b,
  );
  const anchors = new Map<number, number>();
  const shifts: { beat: number; offset: number }[] = [];
  let offset = 0;
  for (const beat of harmonyBeats) {
    let x = baseX(beat) + offset;
    for (const lane of groups) {
      const index = lane.findIndex((c) => c.beat === beat);
      if (index < 0) continue;
      const current = lane[index];
      x = Math.max(x, LEFT_PAD + pad - current.left);
      if (index === 0) continue;
      const previous = lane[index - 1];
      x = Math.max(x, anchors.get(previous.beat)! + previous.width + CHORD_GAP - current.left);
    }
    offset = x - baseX(beat);
    shifts.push({ beat, offset });
    anchors.set(beat, x);
  }
  const shiftAt = (beat: number) => {
    let shift = 0;
    for (const point of shifts) {
      if (point.beat > beat + 1e-6) break;
      shift = point.offset;
    }
    return shift;
  };
  for (const voice of laid.flatMap((lb) => lb.voices)) {
    let principalShift = 0;
    // A grace run stays attached to its following principal when that onset
    // moves; its synthetic pre-onsets do not claim their own harmony space.
    for (let i = voice.length - 1; i >= 0; i--) {
      if (!voice[i].note.grace) principalShift = shiftAt(voice[i].note.beat);
      voice[i].x += principalShift;
    }
  }
  let width = initialWidth + offset;
  for (const lane of groups) {
    const last = lane.at(-1);
    if (last) width = Math.max(width, anchors.get(last.beat)! + last.width + RIGHT_PAD);
  }
  const chordXs = lanes.map((lane) => {
    const ends = new Map<number, number>();
    return lane.map((c) => {
      const x = ends.get(c.beat) ?? anchors.get(c.beat)!;
      ends.set(c.beat, x + chordLabelSpaces(c.text, activeChordStyle) + CHORD_GAP);
      return x;
    });
  });
  return { width, chordXs };
}

/** Pass 5: chord symbols positioned over the note on their beat. */
function layoutChordSymbols(
  bar: Bar,
  width: number,
  laid: LaidNote[],
  chordXs?: readonly number[],
): DrawnMeasure['chordSymbols'] {
  const beats = bar.beats || 4;
  const out = displayedHarmony(bar).map((c, i) => ({
    sourceId: c.sourceId,
    x: chordXs?.[i] ?? xAtBeat(laid, c.beat, beats, width),
    text: c.text,
    beat: c.beat,
  }));
  // Collision cascade: a symbol never starts before its predecessor ends.
  // Onset-interpolated x's compress sparse bars (two chords over one long
  // note used to abut); local reservations provide the aggregate width, so
  // pushing right stays inside the bar.
  for (let i = 1; i < out.length; i++) {
    const prev = out[i - 1];
    const minX = prev.x + chordLabelSpaces(prev.text, activeChordStyle) + CHORD_GAP;
    if (out[i].x < minX) out[i].x = minX;
  }
  // Push-back: the cascade never runs past the barline. Late melody onsets
  // can anchor a chord near the bar's end (Misty's coda: a symbol overran
  // its bar by 13 spaces into the next bar's labels) — walk right→left,
  // clamping each symbol inside the bar while keeping the separation the
  // local reservations provide.
  let maxEnd = width - 0.4;
  for (let i = out.length - 1; i >= 0; i--) {
    const w = chordLabelSpaces(out[i].text, activeChordStyle);
    if (out[i].x + w > maxEnd) out[i].x = Math.max(1.2, maxEnd - w);
    maxEnd = out[i].x - CHORD_GAP;
  }
  return out;
}

/** Place the whole numeral/bracket outside the visible contents of its group.
 * SMuFL glyph bounds are relative to the rendered baseline; head centres alone
 * miss flats, rests and stacked marks. All dimensions remain in staff spaces. */
function placeTuplets(
  pending: PendingTuplet[],
  notes: DrawnNote[],
  rests: DrawnRest[],
  beams: DrawnBeam[],
): DrawnTuplet[] {
  return pending.map(({ figure, fromX, toX }) => {
    let top = Infinity;
    let bottom = -Infinity;
    const see = (lo: number, hi = lo) => {
      top = Math.min(top, lo);
      bottom = Math.max(bottom, hi);
    };
    const glyph = (cp: SmuflGlyph, y: number, scale: number) => {
      const bounds = glyphVerticalBounds(cp);
      see(y + bounds.top * scale, y + bounds.bottom * scale);
    };
    const inSpan = (x: number) => x >= fromX - 1e-6 && x <= toX + 1e-6;
    for (const note of notes.filter((n) => inSpan(n.x))) {
      const scale = note.grace ? ENGRAVING.graceScale : note.cue ? ENGRAVING.cueScale : 1;
      for (const head of note.heads ?? [note]) {
        glyph(note.notehead, head.y, scale);
        if (head.accidental != null) glyph(head.accidental, head.y, scale);
        for (const y of head.ledger)
          see(y - ENGRAVING.staffLineThickness / 2, y + ENGRAVING.staffLineThickness / 2);
      }
      if (note.stem) {
        see(Math.min(note.stem.y1, note.stem.y2), Math.max(note.stem.y1, note.stem.y2));
        if (note.flag != null) glyph(note.flag, note.stem.y2, scale);
      }
      for (const mark of [...(note.artics ?? []), ...(note.ornaments ?? [])]) {
        glyph(mark.glyph, mark.y, scale);
      }
    }
    for (const rest of rests.filter((r) => inSpan(r.x))) {
      const scale = rest.cue ? ENGRAVING.cueScale : 1;
      glyph(rest.glyph, rest.y, scale);
      for (const ornament of rest.ornaments ?? []) glyph(ornament.glyph, ornament.y, scale);
    }
    for (const beam of beams) {
      // Stems attach at a head edge, just outside the onset span.
      const inset = ENGRAVING.noteheadWidth / 2 + 1e-6;
      if (Math.max(beam.x1, beam.x2) < fromX - inset || Math.min(beam.x1, beam.x2) > toX + inset)
        continue;
      const half = (ENGRAVING.beamThickness * (beam.grace ? ENGRAVING.graceScale : 1)) / 2;
      see(Math.min(beam.y1, beam.y2) - half, Math.max(beam.y1, beam.y2) + half);
    }
    const bracket = figure.x1 != null;
    const inset = Math.max(TUPLET_ENGRAVING.halfHeight, bracket ? TUPLET_ENGRAVING.hookLength : 0);
    const gap = inset + TUPLET_ENGRAVING.clearance;
    const y = figure.below ? bottom + gap : top - gap;
    return {
      ...figure,
      y: bracket ? (figure.below ? Math.max(5.5, y) : Math.min(-1.5, y)) : y,
    };
  });
}

/** Emit one laid-out voice's drawn geometry into the shared arrays. */
function emitVoice(
  laid: LaidNote[],
  forceStemUp: boolean | undefined,
  notes: DrawnNote[],
  rests: DrawnRest[],
  beamsOut: DrawnBeam[],
  tupletBatches?: TupletBatch[],
  dynamicsOut?: DrawnDynamic[],
  meter: { groupBeats?: number; barBeats?: number } = {},
  textsOut?: DrawnText[],
  lyricsOut?: DrawnLyric[],
  stemLenScale = 1,
  voice = 0,
  obstacles: readonly BeamObstacle[] = [],
): void {
  const noteStart = notes.length;
  const restStart = rests.length;
  const pendingTuplets: PendingTuplet[] = [];
  const explicitTuplets = tupletRuns(laid);
  const groups = groupBeams(laid, meter.groupBeats ?? 1, explicitTuplets);
  const beamed = new Set(groups.flat());
  const stems = new Map<number, DrawnNote['stem']>();
  const beamBatches: BeamBatch[] = [];
  const vBeams = computeBeams(
    laid,
    groups,
    stems,
    forceStemUp,
    tupletBatches ? pendingTuplets : undefined,
    stemLenScale,
    beamBatches,
  );
  // An INTERIOR grace run whose mini-beam lands near a principal beam FLIPS
  // to the opposite side of its heads (the reference's practice: the Ave
  // Maria's turn hangs stems-down under the stems-up sextuplet beam). The
  // old same-side "duck" squeezed the mini-beam right up against the
  // principal beam, which read as interference.
  const graceGroups = groups.filter((g) => laid[g[0]].note.grace);
  const mainBeams = vBeams.filter((b) => !b.grace);
  for (const group of graceGroups) {
    const gBeams = vBeams.filter(
      (b) =>
        b.grace && b.x1 >= laid[group[0]].x - 1.5 && b.x2 <= laid[group[group.length - 1]].x + 1.5,
    );
    if (gBeams.length === 0) continue;
    let near = false;
    for (const gb of gBeams) {
      for (const p of mainBeams) {
        const ox1 = Math.max(Math.min(gb.x1, gb.x2), Math.min(p.x1, p.x2));
        const ox2 = Math.min(Math.max(gb.x1, gb.x2), Math.max(p.x1, p.x2));
        if (ox2 - ox1 < 0.2) continue;
        const mid = (ox1 + ox2) / 2;
        const yOn = (s: DrawnBeam) => s.y1 + ((mid - s.x1) / (s.x2 - s.x1 || 1)) * (s.y2 - s.y1);
        if (Math.abs(yOn(gb) - yOn(p)) < 1.6) near = true;
      }
    }
    if (!near) continue;
    const firstStem = stems.get(group[0]);
    if (!firstStem) continue;
    const wasUp = Math.min(firstStem.y1, firstStem.y2) < laid[group[0]].y;
    const dir = wasUp ? 1 : -1; // flip to the other side
    const len = ENGRAVING.stemLength * ENGRAVING.graceStemScale;
    const tipYs = group.map((i) => laid[i].y + dir * len);
    const beamY = dir > 0 ? Math.max(...tipYs) : Math.min(...tipYs);
    const stemXOf = (i: number) =>
      laid[i].x +
      (dir < 0 ? ENGRAVING.noteheadWidth / 2 : -ENGRAVING.noteheadWidth / 2) *
        headScaleOf(laid[i].note);
    for (const i of group) stems.set(i, { x: stemXOf(i), y1: laid[i].y, y2: beamY });
    for (const gb of gBeams) {
      const off =
        -dir *
        ((gb.level ?? 1) - 1) *
        (ENGRAVING.beamThickness + ENGRAVING.beamSpacing) *
        ENGRAVING.graceScale;
      gb.x1 = stemXOf(group[0]);
      gb.x2 = stemXOf(group[group.length - 1]);
      gb.y1 = beamY + off;
      gb.y2 = beamY + off;
    }
  }
  // All voices have already been spaced and justified. Clear the complete
  // glyph ink, including neighbouring voices and accidentals, after grace
  // direction changes and before marks and tuplets follow the final stems.
  for (const batch of beamBatches) {
    const firstStem = stems.get(batch.indices[0]);
    if (!firstStem) continue;
    const shift = beamClearanceShift(batch.beams, obstacles, firstStem.y2 < firstStem.y1);
    for (const beam of batch.beams) {
      beam.y1 += shift;
      beam.y2 += shift;
    }
    for (const i of batch.indices) {
      const stem = stems.get(i);
      if (stem) stem.y2 += shift;
    }
  }
  beamsOut.push(...vBeams);
  laid.forEach((n, i) => {
    if (n.note.rest) {
      // A rest spanning the WHOLE bar draws as the whole-rest sign hanging
      // from a line (the bar-rest convention in any meter) — near its own
      // voice's register in multi-voice bars, the standard line otherwise.
      const wholeBar =
        n.note.beat === 1 && meter.barBeats != null && n.note.duration >= meter.barBeats;
      rests.push({
        // Slot rebuilding fills gaps with `r:` rests and overwrite tails with
        // `r-` rests. They are spacing, not authored events to select/delete.
        ...(/^r[:-]/.test(n.note.id) ? {} : { sourceId: n.note.id }),
        glyph: wholeBar ? restFor(RhythmValue.Whole) : restFor(n.value),
        x: n.x,
        y: wholeBar
          ? forceStemUp === undefined
            ? 1
            : Math.max(0, Math.min(3, Math.round(n.y)))
          : n.y,
        beat: n.note.beat,
        ...(voice ? { voice } : {}),
        ...(n.note.cue ? { cue: true } : {}),
        ...(n.note.ornaments?.length ? {
          ornaments: n.note.ornaments.map((kind, k) => ({
            glyph: ornamentGlyph(kind), y: Math.min(n.y - 1, -1.5) - k * 1.4,
          })),
        } : {}),
      });
      if (dynamicsOut && n.note.dynamic) {
        const glyphs = dynamicGlyphs(n.note.dynamic);
        if (glyphs) dynamicsOut.push({ x: n.x, y: Math.max(6.2, n.y + 2), glyphs });
      }
      if (textsOut && n.note.wordsBelow) {
        textsOut.push({ x: n.x, y: Math.max(6.4, n.y + 2.4), text: n.note.wordsBelow });
      }
      if (textsOut && n.note.words) {
        textsOut.push({ x: n.x, y: Math.min(n.y - 2, -2), text: n.note.words });
      }
      return;
    }
    const isBeamed = beamed.has(i);
    // A chord's unbeamed stem runs from the head farthest from the tip,
    // through the stack, to a stem-length beyond the nearest head.
    const anchorY = n.heads
      ? n.stemUp
        ? Math.max(...n.heads.map((h) => h.y))
        : Math.min(...n.heads.map((h) => h.y))
      : n.y;
    const nearY = n.heads
      ? n.stemUp
        ? Math.min(...n.heads.map((h) => h.y))
        : Math.max(...n.heads.map((h) => h.y))
      : n.y;
    const stem = stems.get(i) ?? {
      x:
        n.x +
        (n.stemUp ? headWidthOf(n) / 2 : -headWidthOf(n) / 2) *
          headScaleOf(n.note),
      y1: anchorY + (n.note.slash ? (n.stemUp ? -0.656 : 0.664) * headScaleOf(n.note) : 0),
      y2: nearY + (n.stemUp ? -1 : 1) * ENGRAVING.stemLength * stemScaleOf(n.note),
    };
    const stemUp = stem.y2 < stem.y1;
    const flag = !isBeamed && isBeamable(n.value) ? flagFor(n.value, stemUp) : null;
    // Articulations sit on the notehead side OPPOSITE the stem, stacked
    // outward from the head (staccato first), snapped off staff lines.
    let artics: DrawnNote['artics'];
    if (n.note.articulations?.length) {
      const dir = stemUp ? 1 : -1; // stem up -> below the head
      const edge = n.heads
        ? stemUp
          ? Math.max(...n.heads.map((h) => h.y))
          : Math.min(...n.heads.map((h) => h.y))
        : n.y;
      artics = n.note.articulations.map((kind, k) => {
        let y = edge + dir * (1 + k * 0.9);
        // Inside the staff, a mark on a line is illegible — take the space.
        if (y > -0.5 && y < 4.5 && Math.abs(y - Math.round(y)) < 0.25) {
          y = Math.round(y) + dir * 0.5;
        }
        return { glyph: articulationGlyph(kind, !stemUp), y };
      });
    }
    // Ornaments sit ABOVE the note column whatever the stem does — clear of
    // the head, an up-stem's tip (which may reach a beam), and any above-side
    // articulation stack — and never inside the staff.
    let ornaments: DrawnNote['ornaments'];
    if (n.note.ornaments?.length) {
      const headTop = n.heads ? Math.min(...n.heads.map((h) => h.y)) : n.y;
      const stemTop = stemUp ? Math.min(stem.y1, stem.y2) : headTop;
      const articClear = !stemUp && artics ? Math.min(...artics.map((a) => a.y)) : Infinity;
      const base = Math.min(headTop, stemTop, articClear) - 1.0;
      const top = Math.min(base, -1.5); // at least a space above the staff
      ornaments = n.note.ornaments.map((kind, k) => ({
        glyph: ornamentGlyph(kind),
        y: top - k * 1.4,
      }));
    }
    if (dynamicsOut && n.note.dynamic) {
      const glyphs = dynamicGlyphs(n.note.dynamic);
      // Below the staff, clear of low noteheads.
      if (glyphs) dynamicsOut.push({ x: n.x, y: Math.max(6.2, n.y + 2), glyphs });
    }
    if (textsOut && n.note.wordsBelow) {
      // Below the staff, under low noteheads/down-stems and the dynamics lane.
      const headBot = n.heads ? Math.max(...n.heads.map((h) => h.y)) : n.y;
      const stemBot = !stemUp ? Math.max(stem.y1, stem.y2) : headBot;
      textsOut.push({
        x: n.x,
        y: Math.max(6.4, headBot + 1.6, stemBot + 1.2),
        text: n.note.wordsBelow,
      });
    }
    if (lyricsOut && n.note.lyrics) {
      n.note.lyrics.forEach((syl, verse) => {
        if (syl)
          lyricsOut.push({
            x: n.x,
            verse,
            text: syl.text,
            ...(syl.hyphen ? { hyphen: true } : {}),
          });
      });
    }
    if (textsOut && n.note.words) {
      // Staff text sits ABOVE the column like an ornament: clear of the head,
      // an up-stem's tip, the artic stack and any ornaments, never in-staff.
      const headTop = n.heads ? Math.min(...n.heads.map((h) => h.y)) : n.y;
      const stemTop = stemUp ? Math.min(stem.y1, stem.y2) : headTop;
      const articClear = !stemUp && artics ? Math.min(...artics.map((a) => a.y)) : Infinity;
      const ornClear = ornaments ? Math.min(...ornaments.map((o) => o.y)) - 0.4 : Infinity;
      const base = Math.min(headTop, stemTop, articClear, ornClear) - 1.2;
      textsOut.push({ x: n.x, y: Math.min(base, -2), text: n.note.words });
    }
    notes.push({
      sourceId: n.note.id,
      sourceHeadIndex: n.note.sourceHeadIndex,
      x: n.x,
      y: n.y,
      midi: n.note.midi,
      beat: n.note.beat,
      notehead: n.note.slash
        ? slashNoteheadFor(n.note.slash === 'beat' ? RhythmValue.Quarter : n.value).glyph
        : noteheadFor(n.value),
      ...(n.note.slash ? { noteheadWidth: headWidthOf(n) } : {}),
      stem: n.note.slash === 'beat' || n.value <= RhythmValue.Whole ? undefined : stem,
      flag: n.note.slash === 'beat' ? undefined : flag ?? undefined,
      accidental: n.accidental ?? undefined,
      ...(n.accidentalDx != null ? { accidentalDx: n.accidentalDx } : {}),
      dots: n.rhythm.dots,
      ledger: ledgerLineOffsets(n.offset).map(yFromOffset),
      tieToNext: n.note.tie === 'start' || n.note.tie === 'both' ? true : undefined,
      tieFromPrevious: n.note.tie === 'stop' || n.note.tie === 'both' ? true : undefined,
      ...(voice ? { voice } : {}),
      ...(n.note.grace ? { grace: true } : {}),
      ...(n.note.cue ? { cue: true } : {}),
      ...(artics ? { artics } : {}),
      ...(ornaments ? { ornaments } : {}),
      ...(n.heads
        ? {
            heads: n.heads.map((h) => ({
              y: h.y,
              midi: h.midi,
              sourceHeadIndex: h.sourceHeadIndex,
              ...(h.tie === 'start' || h.tie === 'both' ? { tieToNext: true } : {}),
              ...(h.tie === 'stop' || h.tie === 'both' ? { tieFromPrevious: true } : {}),
              dx: h.dx,
              accidental: h.accidental ?? undefined,
              ...(h.accidentalDx != null ? { accidentalDx: h.accidentalDx } : {}),
              ledger: ledgerLineOffsets(h.offset).map(yFromOffset),
            })),
          }
        : {}),
    });
  });
  // One figure belongs to the complete explicit tuplet. A bare figure is
  // enough only when ONE beam spans EVERY pitched member and there are no rests. Rests,
  // slower values and fragmented groups need a bracket over the full run.
  if (tupletBatches) {
    for (const indices of explicitTuplets) {
      const members = indices.map((i) => laid[i]);
      if (members.length < 2) continue;
      const first = members[0];
      const last = members[members.length - 1];
      const fullyBeamed = members.every((n) => !n.note.rest) && groups.some((group) =>
        group[0] === indices[0] && group[group.length - 1] === indices[indices.length - 1] &&
        indices.every((i) => laid[i].note.rest || group.includes(i)),
      );
      const fromX = first.x - (fullyBeamed ? 0 : 0.7);
      const toX = last.x + (fullyBeamed ? 0 : 0.9);
      pendingTuplets.push({
        figure: {
          x: (fromX + toX) / 2,
          y: 0,
          label: String(first.note.tuplet),
          ...(!fullyBeamed ? { x1: fromX, x2: toX } : {}),
          ...(forceStemUp === false ? { below: true } : {}),
        },
        fromX,
        toX,
      });
    }
    tupletBatches.push({
      pending: pendingTuplets,
      notes: notes.slice(noteStart),
      rests: rests.slice(restStart),
      beams: vBeams,
    });
  }
}

/** A bar's voices laid out (pitch/rhythm/stems/advances) but not yet spaced. */
interface LaidBar {
  /** One laid array per voice, in voice order; index 0 always exists. */
  voices: LaidNote[][];
  multiVoice: boolean;
  /** Per-voice forced stem direction (undefined = natural), decided at layout
   *  time so beam emission agrees with what the notes were laid against. */
  stemUp: (boolean | undefined)[];
  /** MIDDLE voices (neither highest nor lowest register) — their beamed
   *  stems shorten so the beam hugs their own heads. */
  inner: boolean[];
  /** Each laid voice's index in barVoices(bar). CONDENSE drops duplicates,
   *  so the position in `voices` is not a voice's identity across bars. */
  sourceVoice: number[];
}

// Multi-voice engraving. Three rules, all per bar:
// (1) CONDENSE — voices whose bar content is identical (doubled instruments
//     in unison, e.g. oboe+violin on a condensed ensemble staff) draw once;
//     drawing each stacked every glyph and beamed every unison line twice.
// (2) Stem direction follows REGISTER, not voice order: pitched voices rank
//     by mean staff position — the HIGHEST voice stems up, every other voice
//     down (Gould; the reference scores' practice). Document order says
//     nothing about which line sits higher, and up/down alternation pointed
//     a third voice's stems up from the bottom of the texture, its beam
//     climbing through the middle voice. A bar left with ONE pitched voice
//     keeps natural pitch-based stems.
// (3) A voice's rests park near ITS OWN register (mean staff position,
//     nudged a step toward its stem side, clamped near the staff), falling
//     back to a parity ladder around the middle line when the voice is
//     silent all bar. Single-voice bars keep natural stems and mid-line
//     rests.
function layoutBar(bar: Bar, key: string | undefined, clef: Clef, quarterPerBeat = 1): LaidBar {
  const seen = new Set<string>();
  const sourceVoice: number[] = [];
  const sources = barVoices(bar).filter((src, index) => {
    // The signature is the CORE musical content only — ids and per-note
    // annotations (cue words, ornaments, articulations, dynamics) excluded.
    // A doubled unison line carries its "Viol. u. Ob." or trill on ONE copy
    // (the source attaches directions/ornaments to the first voice in
    // document order), and keeping both drew the same melody twice, tangled
    // through the texture. The FIRST occurrence — the annotated one — is
    // kept, so the cue/trill still prints.
    const sig = JSON.stringify(
      src.map((note) => [
        note.midi,
        note.spelledAs,
        note.beat,
        note.duration,
        note.writtenDuration,
        note.tie,
        note.rest,
        note.slash,
        note.grace,
        note.cue,
        note.tuplet,
        note.lyrics,
        note.chordNotes,
      ]),
    );
    if (seen.has(sig)) return false;
    seen.add(sig);
    sourceVoice.push(index);
    return true;
  });
  const multiVoice = sources.length > 1;
  // Mean staffOffset per voice — positive UPWARD (E4 0, B4 2), unlike y.
  const means = sources.map((src): number | null => {
    const pitched = src.filter((n) => !n.rest);
    if (pitched.length === 0) return null;
    return (
      pitched.reduce(
        (sum, n) => sum + (n.slash ? MIDDLE_OFFSET : staffOffset(n.midi, { key, spelledAs: n.spelledAs, clef })),
        0,
      ) / pitched.length
    );
  });
  const ranked = means
    .map((mean, vi) => ({ vi, mean }))
    .filter((v): v is { vi: number; mean: number } => v.mean !== null)
    .sort((a, b) => b.mean - a.mean);
  const rank = new Map(ranked.map((v, i) => [v.vi, i]));
  const stemUp: (boolean | undefined)[] = sources.map((_, vi) => {
    if (!multiVoice) return undefined;
    if (ranked.length === 1 && rank.get(vi) === 0) return undefined;
    // OUTER voices point outward (top up, bottom down); a MIDDLE voice takes
    // the side of its own register — above the middle line stems up (its
    // beam shares the top voice's territory above the texture, the reference
    // scores' practice), below stems down. The old up/down ALTERNATION tied
    // direction to rank parity, which pointed a bottom voice's stems up
    // through the middle voice (Telemann bar 8, user screenshot).
    const r = rank.get(vi) ?? vi;
    if (r === 0) return true;
    if (r === ranked.length - 1) return false;
    return (means[vi] ?? MIDDLE_OFFSET) > MIDDLE_OFFSET;
  });
  const voices = sources.map((source, vi) => {
    if (!multiVoice) return layoutVoice(source, key, { clef, quarterPerBeat });
    const up = stemUp[vi];
    const mean = means[vi];
    let restOffset: number;
    if (mean !== null) {
      const bias = up === undefined ? 0 : up ? 1 : -1;
      restOffset = Math.max(-1, Math.min(5, Math.round(mean) + bias));
    } else {
      // A voice silent all bar: its bar rest stays INSIDE the staff — the
      // up-stem voice's rest hangs near the top line, the down-stem voice's
      // sits near the bottom (the old ladder had the sides swapped and
      // escalated deeper voices right out of the staff, which read as
      // randomly floating rests). Stacked silences separate via the dodge.
      restOffset = MIDDLE_OFFSET + (up ? -2 : 1);
    }
    return layoutVoice(
      source,
      key,
      up === undefined
        ? { restOffset, clef, quarterPerBeat }
        : { forceStemUp: up, restOffset, clef, quarterPerBeat },
    );
  });
  const inner = sources.map((_, vi) => {
    const r = rank.get(vi);
    return multiVoice && r !== undefined && r > 0 && r < ranked.length - 1;
  });
  return { voices, multiVoice, stemUp, inner, sourceVoice };
}

/** Width floor for sparse bars; harmony reserves its own local space. */
function minContentWidth(bar: Bar): number {
  const beats = bar.beats || 4;
  return LEFT_PAD + RIGHT_PAD + beats * EMPTY_BEAT_SPACES;
}

/** Song beats are DENOMINATOR units, but written notation is quarter-based:
 *  quarterPerBeat scales durations so a 3/8 eighth draws as an eighth, and
 *  groupBeats sets the beam-group unit (eighth meters beam in threes — the
 *  whole bar in 3/8, per dotted crotchet in 6/8; quarter meters per beat). */
function meterOf(timeSig: [number, number] | undefined): {
  quarterPerBeat: number;
  groupBeats: number;
} {
  const den = timeSig?.[1] ?? 4;
  return { quarterPerBeat: 4 / den, groupBeats: den === 8 ? 3 : 1 };
}

/** Beams, glyph emission and chord symbols for an already-spaced bar. */
function finishMeasure(
  bar: Bar,
  lb: LaidBar,
  width: number,
  barline: DrawnMeasure['barline'],
  barIndex: number,
  groupBeats = 1,
  horizontalScale = 1,
  chordXs?: readonly number[],
): DrawnMeasure {
  const notes: DrawnNote[] = [];
  const rests: DrawnRest[] = [];
  const beams: DrawnBeam[] = [];
  const tupletBatches: TupletBatch[] = [];
  const dynamics: DrawnDynamic[] = [];
  const texts: DrawnText[] = [];
  const formTexts: DrawnText[] = [];
  const lyrics: DrawnLyric[] = [];
  // An empty bar reads as a bar of rest, not a blank stave: centre a whole-bar
  // rest (hangs from the second line, the engraving convention). A measure-
  // repeat owns that same visual slot, so it replaces the synthetic rest.
  if (!bar.barRepeat && lb.voices.every((v) => v.length === 0)) {
    rests.push({ glyph: restFor(RhythmValue.Whole), x: width / 2, y: 1 });
  }
  // A form-level measure repeat is authoritative: stale authored voice
  // content may remain in the score model while arranging, but it must not
  // collide with or visually contradict the conventional repeat sign.
  if (!bar.barRepeat) {
    const obstacles = beamObstacles(lb.voices.flat());
    lb.voices.forEach((laid, vi) => {
      // A voice resting the WHOLE bar (in a multi-voice bar) collapses to one
      // centred bar rest — the engraving convention, and its written rests
      // sat exactly on the beat-1 column where every other voice's downbeat
      // notes land, where no vertical dodge could clear a dense bar.
      if (lb.multiVoice && laid.length > 0 && laid.every((l) => l.note.rest)) {
        const y = Math.min(4, Math.max(0, laid[0].y));
        const ornaments = laid.flatMap((l) => l.note.ornaments ?? []).map((kind, k) => ({
          glyph: ornamentGlyph(kind), y: Math.min(y - 1, -1.5) - k * 1.4,
        }));
        rests.push({
          glyph: restFor(RhythmValue.Whole),
          x: width / 2,
          y,
          beat: 1,
          ...(lb.sourceVoice[vi] ? { voice: lb.sourceVoice[vi] } : {}),
          ...(ornaments.length ? { ornaments } : {}),
        });
        return;
      }
      emitVoice(
        laid,
        lb.stemUp[vi],
        notes,
        rests,
        beams,
        tupletBatches,
        dynamics,
        {
          groupBeats,
          barBeats: bar.beats || 4,
        },
        texts,
        lyrics,
        lb.inner[vi] ? 0.75 : 1,
        lb.sourceVoice[vi],
        obstacles,
      );
    });
  }

  // Form-level prose is anchored at the measure's first content column. It
  // joins note-attached words in the shared collision pass below, so section
  // cues and performance directions cannot disappear into stems or beams.
  if (bar.staffText) {
    const x = (LEFT_PAD + openRepeatPad(bar)) * horizontalScale;
    const maxChars = Math.max(1, Math.floor((width - x - RIGHT_PAD * horizontalScale) / TEXT_CHAR_W));
    const lines = staffTextLines(bar.staffText, maxChars);
    lines.forEach((text, i) => formTexts.push({
      x,
      y: bar.staffTextAbove ? -2 - (lines.length - 1 - i) * 2.2 : 6.4 + i * 2.2,
      text,
    }));
    // Dodge from the staff outwards. For above cues this means the last
    // line first, so a collision cannot reverse the prose's reading order.
    texts.push(...(bar.staffTextAbove ? [...formTexts].reverse() : formTexts));
  }

  // Rest ink clears every chord head and the completed staff geometry.
  if (lb.multiVoice) positionRests(notes, rests, beams);

  // A multi-voice rest may have moved since emission. Use its final position,
  // but keep each tuplet scoped to its own voice rather than the whole staff.
  const tuplets = tupletBatches.flatMap(({ pending, notes, rests, beams }) =>
    placeTuplets(pending, notes, rests, beams),
  );

  // Staff text dodges everything under its FULL x-extent — the cue was
  // placed clear of its own note, but "Viola" is wider than one column and
  // the group's beam runs right through where it parks. Above-texts climb,
  // below-texts sink (a cue never flips sides); earlier (leftward) texts
  // become obstacles for later ones so neighbours stack instead of merging.
  texts.sort((a, b) => a.x - b.x);
  texts.forEach((t, ti) => {
    const x1 = t.x - 0.4;
    const x2 = t.x + TEXT_CHAR_W * t.text.length + 0.4;
    const inWindow = (x: number, pad = 0) => x > x1 - pad && x < x2 + pad;
    const gapAt = (y: number): number => {
      let worst = Infinity;
      const seg = (lo: number, hi: number, need: number) => {
        const d = y < lo ? lo - y : y > hi ? y - hi : 0;
        worst = Math.min(worst, d / need);
      };
      for (const n of notes) {
        if (inWindow(n.x, 0.7)) seg(n.y, n.y, 1.5);
        const s = n.stem;
        if (s && inWindow(s.x, 0.2)) seg(Math.min(s.y1, s.y2), Math.max(s.y1, s.y2), 1.1);
        if (s && n.flag && inWindow(s.x, 1.0)) {
          const tip = Math.abs(s.y1 - n.y) > Math.abs(s.y2 - n.y) ? s.y1 : s.y2;
          const up = tip < n.y;
          seg(up ? tip : tip - 2.5, up ? tip + 2.5 : tip, 1.1);
        }
      }
      for (const b of beams) {
        const bx1 = Math.min(b.x1, b.x2);
        const bx2 = Math.max(b.x1, b.x2);
        const ox1 = Math.max(x1 - 0.3, bx1);
        const ox2 = Math.min(x2 + 0.3, bx2);
        if (ox2 - ox1 < 0.2) continue;
        const yAt = (x: number) => b.y1 + ((x - b.x1) / (b.x2 - b.x1 || 1)) * (b.y2 - b.y1);
        seg(Math.min(yAt(ox1), yAt(ox2)), Math.max(yAt(ox1), yAt(ox2)), 1.35);
      }
      for (const tuplet of tuplets) {
        const left = tuplet.x1 ?? tuplet.x - TUPLET_ENGRAVING.numberGap;
        const right = tuplet.x2 ?? tuplet.x + TUPLET_ENGRAVING.numberGap;
        if (left < x2 && right > x1) {
          seg(tuplet.y - TUPLET_ENGRAVING.halfHeight, tuplet.y + TUPLET_ENGRAVING.halfHeight, 1.2);
        }
      }
      for (let ui = 0; ui < ti; ui++) {
        const u = texts[ui];
        const ux2 = u.x + TEXT_CHAR_W * u.text.length;
        if (u.x < x2 + 0.3 && ux2 > x1 - 0.3) seg(u.y, u.y, 1.9);
      }
      return worst;
    };
    const dir = t.y <= 3 ? -1 : 1; // above-texts climb, below-texts sink
    let y = t.y;
    const formText = formTexts.includes(t);
    for (let k = 0; k <= (formText ? 120 : 12) && gapAt(y) < 1; k++) y = t.y + dir * k * 0.5;
    // Clamp only what the DODGE moved — a placement already past the band
    // (below long down-stems, e.g. "col Pedale" under a low piano octave)
    // was computed to clear them; dragging it back in re-collides.
    t.y = formText ? y : dir < 0 ? Math.max(Math.min(-9, t.y), y) : Math.min(Math.max(15, t.y), y);
  });
  // Keep DOM/accessibility text in source order after the above-cue dodge.
  const formTextSlots = texts.flatMap((text, i) => formTexts.includes(text) ? [i] : []);
  formTextSlots.forEach((slot, i) => { texts[slot] = formTexts[i]; });

  const emptyBeat1X =
    (LEFT_PAD + ENGRAVING.noteheadWidth / 2 + openRepeatPad(bar)) * horizontalScale;
  return {
    x: 0,
    width,
    // Predict the centre of a normal beat-1 notehead using the exact padding
    // and system-justification scale that a subsequently entered note gets.
    // Forward-repeat furniture shifts the first onset farther into the bar.
    emptyBeat1X,
    emptyBeat1CaptureX: emptyBeat1X + ACCIDENTAL_WIDTH * horizontalScale,
    barIndex,
    beats: bar.beats || 4,
    notes,
    rests,
    beams,
    tuplets,
    dynamics,
    texts,
    lyrics,
    chordSymbols: layoutChordSymbols(bar, width, lb.voices[0], chordXs),
    // The bar's own decoration wins over the positional default (single/final).
    barline: bar.closeBarline ?? barline,
    ...(bar.openBarline ? { openBarline: bar.openBarline } : {}),
    ...(bar.ending != null ? { ending: bar.ending } : {}),
    ...(bar.overfull ? { overfull: true } : {}),
    ...(bar.barRepeat ? { barRepeat: bar.barRepeat } : {}),
    ...(bar.directive ? { directive: bar.directive } : {}),
    ...(bar.feel ? { feel: bar.feel } : {}),
    ...(bar.segno ? { segno: true } : {}),
    ...(bar.coda ? { coda: true } : {}),
  };
}

/** A bar laid out and measured, but its drawn geometry not yet emitted — the
 *  seam where justification can stretch note positions before stems, beams
 *  and chord symbols derive from them. */
interface PreparedMeasure {
  bar: Bar;
  lb: LaidBar;
  width: number;
  chordXs: number[];
  barline: DrawnMeasure['barline'];
  barIndex: number;
  /** Beam-group unit in Song beats (from the meter), consumed at finish time. */
  groupBeats: number;
}

/** Leading room a forward-repeat barline needs: its thick+thin+dots furniture
 *  reaches ~1.75 spaces into the bar, past the plain 1.5-space LEFT_PAD. */
function openRepeatPad(bar: Bar): number {
  return bar.openBarline === 'repeat' ? 1.3 : 0;
}

function prepareMeasure(
  bar: Bar,
  key: string | undefined,
  barline: DrawnMeasure['barline'],
  barIndex: number,
  clef: Clef = 'treble',
  minWidth = 0,
  meter = { quarterPerBeat: 1, groupBeats: 1 },
  extents: { beat: number; left: number; right: number }[] = [],
): PreparedMeasure {
  const lb = layoutBar(bar, key, clef, meter.quarterPerBeat);
  const contentW = lb.multiVoice ? spaceColumns(lb.voices) : spaceHorizontally(lb.voices[0]);
  const pad = openRepeatPad(bar);
  if (pad) for (const v of lb.voices) for (const n of v) n.x += pad;
  const minimum = Math.max(contentW + pad, minContentWidth(bar) + pad, minWidth);
  const { width, chordXs } = reserveHarmonySpace([bar], [lb], minimum, extents);
  return { bar, lb, width, chordXs: chordXs[0], barline, barIndex, groupBeats: meter.groupBeats };
}

/**
 * x for a playback position within a DRAWN measure, in staff-spaces from the
 * measure origin. The playhead must land on the note (or chord stack) actually
 * sounding, and note spacing is duration-based (sub-linear), not proportional —
 * so interpolate piecewise between the measure's drawn events (notes, melody
 * rests, chord symbols) rather than sweeping a linear beat fraction. Between
 * the last event and the barline the sweep resumes linearly.
 */
export function xAtBeatInMeasure(m: DrawnMeasure, beat: number): number {
  const pts = beatPointsInMeasure(m);
  const t = Math.min(Math.max(beat, 1), m.beats + 1);
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const z = pts[i + 1];
    if (t >= a.b && t <= z.b) {
      return z.b === a.b ? a.x : a.x + ((t - a.b) / (z.b - a.b)) * (z.x - a.x);
    }
  }
  return pts[pts.length - 1].x;
}

/**
 * Inverse of xAtBeatInMeasure for score-entry hit testing. The returned
 * fraction keeps StaffClick's public 0..1 contract, but follows the drawn
 * onset columns instead of treating the whole barline span as uniform time.
 */
export function beatFractionAtXInMeasure(m: DrawnMeasure, x: number): number {
  // The first notehead can move right by one accidental column (a B-natural
  // in F, for example). Keep that entire engraving zone magnetic to beat 1;
  // otherwise an eighth-note grid interprets the clicked notehead as beat 1½.
  if (x <= m.emptyBeat1CaptureX + 1e-7) return 0;
  const pts = beatPointsInMeasure(m);
  if (x <= pts[0].x) return 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const z = pts[i + 1];
    if (x <= z.x) {
      const beat = Math.abs(z.x - a.x) < 1e-9 ? a.b : a.b + ((x - a.x) / (z.x - a.x)) * (z.b - a.b);
      return Math.min(1, Math.max(0, (beat - 1) / m.beats));
    }
  }
  return 1;
}

/** Shared temporal columns for the entry caret and mouse inverse. */
function beatPointsInMeasure(m: DrawnMeasure): { b: number; x: number }[] {
  const anchors = new Map<number, number>();
  // Chord symbols first so real note geometry (same beat) overwrites them.
  for (const c of m.chordSymbols) anchors.set(c.beat, c.x);
  for (const r of m.rests) if (r.beat !== undefined) anchors.set(r.beat, r.x);
  for (const n of m.notes) anchors.set(n.beat, n.x);
  const pts = [...anchors.entries()].map(([b, x]) => ({ b, x })).sort((a, z) => a.b - z.b);
  pts.push({ b: m.beats + 1, x: m.width }); // the next downbeat is the barline
  // A silent downbeat uses the position where a newly entered, unaltered
  // notehead will actually engrave. This is especially important in a truly
  // empty bar, whose centred whole-bar rest is not a temporal anchor.
  if (pts[0].b > 1) pts.unshift({ b: 1, x: m.emptyBeat1X });
  return pts;
}

// A final system justifies only when already mostly full — a short last line
// should end where its music ends, not crawl across the whole page.
const LAST_SYSTEM_JUSTIFY_MIN = 0.65;

/**
 * Pass 6: greedy system breaking + justification. Rows fill greedily on the
 * bars' natural widths, then every full row stretches to exactly systemWidth,
 * so the right margin is a straight line instead of each system ending
 * wherever its bars ran out (the ragged look). Stretching happens BEFORE
 * finishMeasure: the laid note x's scale together with the bar widths, so
 * stems, beams, dots and chord symbols all derive from the stretched
 * positions and nothing drifts off its notehead. Parallel staves (the grand
 * staff) share one plan — their bars have identical widths by construction
 * and receive identical factors, so cross-staff onset alignment survives.
 * A line plan (barsPerLine) replaces the greedy fill: each line holds the
 * bars it names and stretches to its fill of the width.
 */
function layoutSystems(
  staves: PreparedMeasure[][],
  systemWidth: number,
  firstSystemIndent = 0,
  maxBarsPerSystem?: number,
  lines?: readonly PlannedLine[],
): DrawnSystem[][] {
  const budgetFor = (rowIndex: number) => systemWidth - (rowIndex === 0 ? firstSystemIndent : 0);
  const lead = staves[0];
  const rows: number[][] = [];
  if (lines) {
    for (const line of lines)
      rows.push(Array.from({ length: line.end - line.start }, (_, k) => line.start + k));
  } else {
    let row: number[] = [];
    let x = 0;
    lead.forEach((p, i) => {
      // Lead-sheet mode caps the bar count (the book's four-to-a-line grid);
      // width remains the hard constraint — a dense row still breaks early.
      const full =
        (row.length > 0 && x + p.width > budgetFor(rows.length)) ||
        (maxBarsPerSystem != null && row.length >= maxBarsPerSystem);
      if (full && row.length > 0) {
        rows.push(row);
        row = [];
        x = 0;
      }
      row.push(i);
      x += p.width;
    });
    if (row.length > 0) rows.push(row);
  }

  const out: DrawnSystem[][] = staves.map(() => []);
  rows.forEach((indices, ri) => {
    const natural = indices.reduce((sum, i) => sum + lead[i].width, 0);
    const isLast = ri === rows.length - 1;
    // Reaching the lead-sheet bar cap makes this a complete grid row even when
    // its sparse notation uses less than 65% of the available width. Only a
    // genuinely partial final row should retain its natural, ragged width.
    const reachesBarCap = maxBarsPerSystem != null && indices.length >= maxBarsPerSystem;
    // A planned line stretches to its share of the width: a short one keeps
    // the grid's bar width instead of spreading two bars across the page.
    const budget = budgetFor(ri) * (lines ? lines[ri].fill : 1);
    const stretch =
      natural < budget &&
      (lines != null || !(isLast && !reachesBarCap && natural < LAST_SYSTEM_JUSTIFY_MIN * budget))
        ? budget / natural
        : 1;
    staves.forEach((prepared, si) => {
      let mx = 0;
      const measures = indices.map((i) => {
        const p = prepared[i];
        if (stretch !== 1)
          for (const n of p.lb.voices.flat()) {
            const dx = n.columnDx ?? 0;
            n.x = (n.x - dx) * stretch + dx;
          }
        const m = finishMeasure(
          p.bar,
          p.lb,
          p.width * stretch,
          p.barline,
          p.barIndex,
          p.groupBeats,
          stretch,
          p.chordXs.map((x) => x * stretch),
        );
        m.x = mx;
        mx += m.width;
        return m;
      });
      out[si].push({ measures, width: stretch === 1 ? mx : budget });
    });
  });
  return out;
}

// Per-clef staff furniture: glyph, its anchor line (counted from the TOP
// line, matching DrawnStaff.clefY), and how far DOWN the treble key-signature
// pattern shifts on this staff (staff-spaces). treble8vb is the guitar-
// written G clef (sounds an octave lower, writes identically) — the app's
// own charts read on it; an imported vocal/instrument part on plain treble
// draws the plain G clef. Both C clefs share the SMuFL C-clef glyph anchored
// on their C4 line.
const CLEF_FURNITURE: Record<Clef, { glyph: number; y: number; keyShift: number }> = {
  treble: { glyph: SMUFL.gClef, y: 3, keyShift: 0 },
  treble8vb: { glyph: SMUFL.gClef8vb, y: 3, keyShift: 0 },
  bass: { glyph: SMUFL.fClef, y: 1, keyShift: 1 },
  alto: { glyph: SMUFL.cClef, y: 2, keyShift: 0.5 },
  tenor: { glyph: SMUFL.cClef, y: 1, keyShift: -0.5 },
};

function buildStaff(
  systems: DrawnSystem[],
  key: string | undefined,
  clef: Clef,
  spaceSize: number,
  timeSig: [number, number],
): DrawnStaff {
  const furniture = CLEF_FURNITURE[clef];
  return {
    spaceSize,
    staffLines: STAFF_LINES,
    clef: furniture.glyph,
    clefName: clef,
    clefY: furniture.y,
    // The key-signature pattern is the treble shape shifted per clef. The
    // tenor staff's convention drops any accidental the shift would push
    // above the top line down an octave (the classic zig on tenor sharps).
    keySignature: keySignature(key).map((a) => {
      let y = a.y + furniture.keyShift;
      if (clef === 'tenor' && y < 0) y += 3.5;
      return { ...a, y };
    }),
    timeSig,
    systems,
  };
}

/**
 * The size and line plan a lead sheet engraves at. Without barsPerLine, the
 * options' own size and width and greedy breaking. With it, lines of that many
 * full bars fitted to the page: `widths` are the bars' natural widths, and
 * `song` (the main staff's) names the sections, endings and codas.
 */
function planSystemLines(
  widths: readonly number[],
  song: Song,
  opts: EngraveOptions,
): { spaceSize: number; systemWidth: number; lines?: PlannedLine[] } {
  const spaceSize = opts.spaceSize ?? 10;
  const systemWidth = opts.systemWidth ?? 120;
  if (!opts.barsPerLine || widths.length === 0) return { spaceSize, systemWidth };
  const bars = lineBars(song.sections, song.timeSignature).slice(0, widths.length);
  // A lower staff longer than the main one: its extra bars carry no marks.
  while (bars.length < widths.length)
    bars.push({ pickup: false, lineStart: false, firstEnding: false, laterEnding: false });
  const indent = opts.firstSystemIndent ?? 0;
  const fit = opts.lineFit;
  if (!fit)
    return fitLines(bars, opts.barsPerLine, widths, indent, {
      widthAt: () => systemWidth,
      spaceSize,
      floorSpaceSize: spaceSize,
    });
  return scaledLineFit(bars, opts.barsPerLine, widths, indent, {
    widthAt: (size) => systemWidthAt(fit.geometry, size),
    spaceSize,
    baseSpaceSize: fit.baseSpaceSize,
  });
}

/** Engrave a whole song into positioned staff geometry. */
export function engraveStaff(song: Song, opts: EngraveOptions = {}): DrawnStaff {
  activeChordStyle = opts.chordSymbolStyle ?? 'triangle';
  const key = opts.key ?? song.key;

  const clef = opts.clef ?? 'treble';
  const allBars: Bar[] = song.sections.flatMap((s) => s.bars);
  const prepared = allBars.map((bar, i) =>
    prepareMeasure(
      bar,
      key,
      i === allBars.length - 1 ? 'final' : 'single',
      i,
      clef,
      opts.minMeasureWidths?.[i] ?? 0,
      meterOf(bar.timeSig ?? song.timeSignature),
      opts.harmonyExtents?.filter((e) => e.barIndex === i),
    ),
  );

  const plan = planSystemLines(
    prepared.map((p) => p.width),
    song,
    opts,
  );
  const [systems] = layoutSystems(
    [prepared],
    plan.systemWidth,
    opts.firstSystemIndent,
    opts.maxBarsPerSystem,
    plan.lines,
  );
  return buildStaff(systems, key, clef, plan.spaceSize, song.timeSignature);
}

/** One staff of a jointly-engraved stack: a song and the clef it reads on. */
export interface StaffInput {
  song: Song;
  clef?: Clef;
}

/**
 * Engrave N staves as ONE score: every bar shares one onset-column grid
 * across all staves' voices, so a note sounding on a beat sits at the same x
 * on every staff — a cello minim on beat 3 lines up under the violin's
 * beat 3, not wherever its own sequence would have put it. Bars get
 * identical widths on every staff, so greedy system breaking (deterministic
 * over equal widths) agrees, and system si of staff k is bar-for-bar aligned
 * with system si of every other staff. This is the grand staff generalised
 * to the full score. Each staff reads in its OWN song's key — a transposing
 * part's written key is not the concert part's — unless opts.key forces one.
 */
export function engraveStaves(staves: StaffInput[], opts: EngraveOptions = {}): DrawnStaff[] {
  if (staves.length === 0) return [];
  activeChordStyle = opts.chordSymbolStyle ?? 'triangle';
  const keys = staves.map((st) => opts.key ?? st.song.key);

  const barsPer: Bar[][] = staves.map((st) => st.song.sections.flatMap((s) => s.bars));
  const count = Math.max(...barsPer.map((b) => b.length));
  const emptyBar = (i: number): Bar => {
    const neighbour = barsPer.find((b) => b[i])?.[i];
    return {
      id: `empty-${i}`,
      beats: neighbour?.beats ?? 4,
      chords: [],
      ...(neighbour?.timeSig ? { timeSig: neighbour.timeSig } : {}),
    };
  };

  const prepared: PreparedMeasure[][] = staves.map(() => []);
  for (let i = 0; i < count; i++) {
    const bars = barsPer.map((b) => b[i] ?? emptyBar(i));
    // Each bar is written in its own meter (a 4/4 -> 6/8 change halves the
    // written value of a beat).
    const meters = bars.map((bar, k) => meterOf(bar.timeSig ?? staves[k].song.timeSignature));
    const laid = bars.map((bar, k) =>
      layoutBar(bar, keys[k], staves[k].clef ?? 'treble', meters[k].quarterPerBeat),
    );
    // ONE column grid across every voice of every staff — but step-asides
    // only apply within a staff (see spaceColumns).
    const staffOf = laid.flatMap((lb, k) => lb.voices.map(() => k));
    const jointWidth = spaceColumns(
      laid.flatMap((lb) => lb.voices),
      staffOf,
    );
    // The form's barlines apply to every staff alike, so one pad fits all.
    const pad = Math.max(...bars.map(openRepeatPad));
    if (pad) laid.forEach((lb) => lb.voices.forEach((v) => v.forEach((n) => (n.x += pad))));
    const minimum = Math.max(
      jointWidth + pad,
      ...bars.map((b) => minContentWidth(b) + pad),
      opts.minMeasureWidths?.[i] ?? 0,
    );
    const { width, chordXs } = reserveHarmonySpace(
      bars,
      laid,
      minimum,
      opts.harmonyExtents?.filter((e) => e.barIndex === i),
    );
    const barline = i === count - 1 ? ('final' as const) : ('single' as const);
    laid.forEach((lb, k) =>
      prepared[k].push({
        bar: bars[k],
        lb,
        width,
        chordXs: chordXs[k],
        barline,
        barIndex: i,
        groupBeats: meters[k].groupBeats,
      }),
    );
  }

  // Every staff's bars have the same widths, so the main staff's plan fits all.
  const plan = planSystemLines(
    prepared[0].map((p) => p.width),
    staves[0].song,
    opts,
  );
  const systemsPer = layoutSystems(
    prepared,
    plan.systemWidth,
    opts.firstSystemIndent,
    opts.maxBarsPerSystem,
    plan.lines,
  );
  return systemsPer.map((systems, k) =>
    buildStaff(
      systems,
      keys[k],
      staves[k].clef ?? 'treble',
      plan.spaceSize,
      staves[k].song.timeSignature,
    ),
  );
}

/** The two-staff special case, kept for the piano grand staff callers. */
export function engraveGrandStaff(
  trebleSong: Song,
  bassSong: Song,
  opts: EngraveOptions = {},
): { treble: DrawnStaff; bass: DrawnStaff } {
  const [treble, bass] = engraveStaves(
    [
      { song: trebleSong, clef: 'treble' },
      { song: bassSong, clef: 'bass' },
    ],
    opts,
  );
  return { treble, bass };
}
